import { useRef, useState, useEffect } from 'react';

import { faPlus, faTrashAlt } from '@fortawesome/pro-light-svg-icons';
import classNames from 'classnames';
import { cloneDeep } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useRecoilState, useSetRecoilState } from 'recoil';

import { Duty, WorkWeek } from './types';
import { WorkAgreementDetails } from 'components/types';

import { getWeekDates, WeekDays } from 'utils/date';
import { useStateCallback } from 'utils/reactHooks';

import { hasChangesState, isEditModeState } from './atoms';

import IconButton from '@mui/material/IconButton';
import Slide from '@mui/material/Slide';
import Tooltip from '@mui/material/Tooltip';

import { ArrowNavigation, FontAwesomeSvgIcon } from 'components/shared';

import WorkDay from './WorkDay';
import WorkPatternDetails from './WorkPatternDetails';
import WorkPatternStyles from './WorkPatternStyles';

type Props = {
  workPatternId: number | null;
  weeks: WorkWeek[];
  startDate: string;
  endDate?: string | null;
  workAgreements: WorkAgreementDetails[];
  employeeId?: number;
  canEdit: boolean;
  onRefreshWorkPatterns: () => void;
  onCancelEdit: () => void;
};

export default function WorkPattern(props: Props) {
  const {
    workPatternId,
    weeks: initialWeeks,
    startDate,
    endDate,
    workAgreements,
    employeeId,
    canEdit,
    onRefreshWorkPatterns,
    onCancelEdit: onCancelEdit_
  } = props;
  const { t } = useTranslation('common');
  const [weeks, setWeeks] = useStateCallback<WorkWeek[]>(
    cloneDeep(initialWeeks)
  );
  const [isEditMode] = useRecoilState(isEditModeState);
  const setHasChanges = useSetRecoilState(hasChangesState);
  const [currentWeek, setCurrentWeek] = useState(1);
  const weeksContainerRef = useRef(document.createElement('div'));
  const [updatedStartDate, setUpdatedStartDate] = useState<Date | null>(null);

  const onDutiesReceived = (date: Date, duties: Duty[], weekNr) => {
    const weekIndex = weekNr - 1;
    const week = {
      ...weeks[weekIndex],
      [WeekDays[date.getDay()].toLowerCase()]: duties
    };
    setWeeks([
      ...weeks.slice(0, weekIndex),
      week,
      ...weeks.slice(weekIndex + 1)
    ]);
    setHasChanges(true);
  };

  const addWeek = () => {
    const weekNr = weeks.length + 1;
    setWeeks([...weeks, { nr: weekNr }], () => {
      setCurrentWeek(weekNr);
    });
    setHasChanges(true);
  };

  const removeWeek = () => {
    const newWeeks = weeks.filter(({ nr }) => nr !== currentWeek);
    newWeeks.forEach((week, i) => {
      week.nr = i + 1;
    });
    setWeeks(newWeeks, () => {
      setCurrentWeek(
        currentWeek <= newWeeks.length ? currentWeek : newWeeks.length
      );
    });
    setHasChanges(true);
  };

  const onCancelEdit = () => {
    if (!workPatternId) {
      onCancelEdit_();
    } else {
      setWeeks(cloneDeep(initialWeeks));
      if (weeks.length !== initialWeeks.length) {
        setCurrentWeek(1);
      }
    }
  };

  const [isInEmployeeCardDialog, setIsInEmployeeCardDialog] = useState(false);

  useEffect(() => {
    const employeeCardDialog = document.querySelector<HTMLElement>(
      '.employeeCardDialog'
    );

    if (employeeCardDialog) {
      const dijitDialogTitleBar = employeeCardDialog.querySelector<HTMLElement>(
        '.dijitDialogTitleBar'
      );

      if (dijitDialogTitleBar) {
        dijitDialogTitleBar.style.zIndex = '1';
      }

      setIsInEmployeeCardDialog(true);
    }

    return () => {
      setIsInEmployeeCardDialog(false);
    };
  }, []);

  return (
    <WorkPatternStyles className="work-pattern">
      <div className="wp-header d-flex v-center">
        <div className="d-flex v-center">
          <span className="current-week">
            <span className="semibold">
              {t('week')} {currentWeek}{' '}
            </span>
            {weeks.length > 1 && ` / ${weeks.length}`}
          </span>
          {weeks.length > 1 && (
            <ArrowNavigation
              options={weeks.map((week) => week.nr)}
              selectedOption={currentWeek}
              onNavigate={(dir) =>
                setCurrentWeek(
                  dir === 'prev' ? currentWeek - 1 : currentWeek + 1
                )
              }
            />
          )}
        </div>
        {canEdit && isEditMode && (
          <div className="actions">
            <Tooltip title={t('addWeek') || ''}>
              <IconButton size="small" color="primary" onClick={addWeek}>
                <FontAwesomeSvgIcon icon={faPlus} fontSize="small" />
              </IconButton>
            </Tooltip>
            {weeks.length > 1 && (
              <Tooltip title={`${t('remove')} ${t('week')} ${currentWeek}`}>
                <IconButton size="small" color="error" onClick={removeWeek}>
                  <FontAwesomeSvgIcon icon={faTrashAlt} fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </div>
        )}
      </div>
      <div className="content d-flex">
        <div className="weeks" ref={weeksContainerRef}>
          {weeks.map((week) => (
            <Slide
              key={week.nr}
              direction={currentWeek < week.nr ? 'left' : 'right'}
              in={week.nr === currentWeek}
              container={weeksContainerRef.current}
              style={{
                transitionDelay: week.nr === currentWeek ? '50ms' : '0ms'
              }}
            >
              <div
                className={classNames('week', {
                  selected: week.nr === currentWeek
                })}
              >
                {getWeekDates().map((date) => (
                  <WorkDay
                    key={+date}
                    date={date}
                    duties={week[WeekDays[date.getDay()].toLowerCase()]}
                    updateDuties={(duties) =>
                      onDutiesReceived(date, duties, week.nr)
                    }
                    isEditMode={isEditMode}
                    workAgreements={workAgreements}
                    startDate={new Date(updatedStartDate || startDate)}
                    endDate={endDate != null ? new Date(endDate) : null}
                    isInEmployeeCardDialog={isInEmployeeCardDialog}
                  />
                ))}
              </div>
            </Slide>
          ))}
        </div>

        <WorkPatternDetails
          workPatternId={workPatternId}
          weeks={weeks}
          startDate={startDate}
          endDate={endDate}
          onRefreshWorkPatterns={onRefreshWorkPatterns}
          onCancelEdit={onCancelEdit}
          workAgreements={workAgreements}
          employeeId={employeeId}
          canEdit={canEdit}
          onChangeStartDate={(date) => {
            setUpdatedStartDate(date);
          }}
        />
      </div>
    </WorkPatternStyles>
  );
}
